import React, { Dispatch, SetStateAction, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { PeriodicReportingType } from '../PeriodicReporting';
import { createStatementByProjectId } from 'lib/api_ts/periodicReportingApi';
import { useLocation } from 'react-router-dom-v5-compat';
import StatusBar from 'components/StatusBar';
import { getProject } from '@lib/api_ts/projectApi';
import { useQuery } from '@tanstack/react-query';

interface PeriodicReportingCreateModalProps {
	isModalOpen: boolean;
	setIsModalOpen: Dispatch<SetStateAction<boolean>>;
	fetchData: () => void;
}

const PeriodicReportingCreateModal: React.FC<PeriodicReportingCreateModalProps> = ({ isModalOpen, setIsModalOpen, fetchData }) => {
	const { pathname } = useLocation();
	const projectId = pathname.split('/')[2];

	const { data: projectData } = useQuery({
		queryKey: ['project', projectId],
		queryFn: () => getProject(projectId),
		enabled: projectId !== undefined,
		refetchOnMount: false,
	});

	const blankReportToCreate = {
		startDate: '',
		endDate: '',
	};
	const [reportToCreate, setReportToCreate] = useState<Partial<PeriodicReportingType>>(blankReportToCreate);
	const resetReportToCreate = () => {
		setReportToCreate(blankReportToCreate);
	};

	const blankStatusBar = {
		status: undefined,
		message: '',
		timeout: undefined,
	};
	const [statusBar, setStatusBar] = useState(blankStatusBar);
	const resetStatusBar = () => {
		setStatusBar(blankStatusBar);
	};

	const blankFieldErrors = {
		startDate: false,
		endDate: false,
	};
	const [fieldErrors, setFieldErrors] = useState(blankFieldErrors);
	const resetFieldErrors = () => {
		setFieldErrors(blankFieldErrors);
	};

	const handleCreate = async () => {
		const isValid = !!reportToCreate.startDate && !!reportToCreate.endDate;
		if (!isValid) {
			setFieldErrors({
				startDate: !reportToCreate.startDate,
				endDate: !reportToCreate.endDate,
			});
			return;
		}

		if (
			new Date(reportToCreate.startDate) < new Date(projectData?.forecastStartDate) ||
			new Date(reportToCreate.endDate) < new Date(projectData?.forecastStartDate)
		) {
			setStatusBar({
				status: 'error',
				message: 'Le date inserite non possono essere precedenti alla data di avvio del progetto',
				timeout: false,
			});
			return;
		}

		if (reportToCreate.startDate)
			setStatusBar({
				status: 'elaborating',
				message: 'Creazione Periodic Reporting in corso',
				timeout: false,
			});
		try {
			await createStatementByProjectId(projectId, {
				statementTypeCode: 'PR',
				reportingStartDate: reportToCreate.startDate,
				reportingEndDate: reportToCreate.endDate,
			});
			setStatusBar({
				status: 'success',
				message: 'Periodic Reporting creato con successo',
				timeout: true,
			});
			setTimeout(() => {
				setIsModalOpen(false);
				resetStatusBar();
				resetReportToCreate();
				fetchData();
			}, 2000);
		} catch (error) {
			console.error('error', error);
			setStatusBar({
				status: 'error',
				message: 'Errore durante la creazione del Periodic Reporting',
				timeout: true,
			});
			setTimeout(() => {
				resetStatusBar();
			}, 2000);
		}
	};

	const dates = [
		{
			name: 'startDate',
			label: 'Data Prevista Inizio',
		},
		{
			name: 'endDate',
			label: 'Data Prevista Fine',
		},
	];
	return (
		<Modal
			size="lg"
			show={isModalOpen}
			onHide={() => {
				setIsModalOpen(false);
				resetReportToCreate();
				resetStatusBar();
				resetFieldErrors();
			}}
			centered
		>
			<Modal.Header closeButton>
				<Modal.Title>Nuovo Periodic Reporting</Modal.Title>
			</Modal.Header>
			<Form>
				<Modal.Body>
					<Row className="mb-3" style={{ marginTop: '2rem' }}>
						{dates.map((date) => (
							<Form.Group key={date.name} as={Col} md={6}>
								<Form.Label className="mb-3">
									{date.label} <span className="required-field">*</span>
								</Form.Label>
								<Form.Control
									type="date"
									value={reportToCreate[date.name] || ''}
									onChange={(event) => {
										setReportToCreate((prevReportToCreate) => ({
											...prevReportToCreate,
											[date.name]: event.target.value,
										}));

										setFieldErrors((prevFieldErrors) => ({
											...prevFieldErrors,
											[date.name]: false,
										}));
									}}
									isInvalid={fieldErrors[date.name]}
								/>
								<Form.Control.Feedback type="invalid">Campo obbligatorio</Form.Control.Feedback>
							</Form.Group>
						))}
						{/* <Form.Group as={Col} md={6}>
							<Form.Label className="mb-3">
								Data Prevista Inizio <span className="required-field">*</span>
							</Form.Label>
							<Form.Control
								type="date"
								value={reportToCreate.startDate || ''}
								onChange={(event) => {
									setReportToCreate((prevReportToCreate) => ({
										...prevReportToCreate,
										startDate: event.target.value,
									}));
									setFieldErrors((prevFieldErrors) => ({
										...prevFieldErrors,
										startDate: false,
									}));
								}}
								isInvalid={fieldErrors.startDate}
							/>
							<Form.Control.Feedback type="invalid">Campo obbligatorio</Form.Control.Feedback>
						</Form.Group>
						<Form.Group as={Col} md={6}>
							<Form.Label className="mb-3">
								Data Prevista fine <span className="required-field">*</span>
							</Form.Label>
							<Form.Control
								type="date"
								value={reportToCreate.endDate || ''}
								onChange={(event) => {
									setReportToCreate((prevReportToCreate) => ({
										...prevReportToCreate,
										endDate: event.target.value,
									}));
									setFieldErrors((prevFieldErrors) => ({
										...prevFieldErrors,
										endDate: false,
									}));
								}}
								isInvalid={fieldErrors.endDate}
							/>
							<Form.Control.Feedback type="invalid">Campo obbligatorio</Form.Control.Feedback>
						</Form.Group> */}
					</Row>
					{statusBar.status ? (
						<StatusBar status={statusBar.status} message={statusBar.message} timeout={statusBar.timeout}>
							<></>
						</StatusBar>
					) : null}
				</Modal.Body>
				<Modal.Footer>
					<Button
						disabled={Object.values(fieldErrors).some(Boolean) || statusBar.status === 'elaborating'}
						onClick={(event) => {
							event.preventDefault();
							handleCreate();
						}}
					>
						Crea
					</Button>
				</Modal.Footer>
			</Form>
		</Modal>
	);
};

export default PeriodicReportingCreateModal;
